/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import * as React from "react";
import { useState } from "react";

import { usePopperTooltip } from "react-popper-tooltip";
import styled from "styled-components";

import { Layer } from "@vienna/common";

import { isTouchDevice } from "src/lib/utils/device";

interface Props {
  children: React.ReactNode;
  tooltipText: string | JSX.Element;
  delayShow?: number;
  delayHide?: number;
  left?: number;
  top?: number;
}

export const Tooltip = ({ children, tooltipText, delayShow, delayHide }: Props) => {
  const [display, setDisplay] = useState<boolean>(false);
  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
    trigger: "hover",
    placement: "bottom",
    closeOnOutsideClick: false,
    visible: display,
    onVisibleChange: setDisplay,
    delayShow: delayShow || 800,
    delayHide: delayHide || 200,
    followCursor: false,
  });

  return (
    <>
      {!isTouchDevice() && (
        <div ref={setTriggerRef} style={{ display: "inline-flex" }}>
          {children}
          {visible && (
            <Wrapper
              ref={setTooltipRef}
              {...getTooltipProps({ className: "tooltip-container" })}
              contentEditable={false}
            >
              <TooltipWrapper>
                <StyledTooltipSmall>{tooltipText}</StyledTooltipSmall>
                <div {...getArrowProps({ className: "tooltip-arrow" })} />
              </TooltipWrapper>
            </Wrapper>
          )}
        </div>
      )}
    </>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  z-index: ${Layer.WIN_LAYER};
`;

const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  cursor: default;
`;

const StyledTooltipSmall = styled.div`
  position: static;
  left: 50%;
  color: #ffffff;
  background: #333333;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 6px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
`;
